import { Transformers, Utils } from '@enterprise/common';
import { InvoiceItem, InvoiceItemChild, InvoiceItemData, InvoiceItemWithVariantDetail } from '../models/datasource/invoiceItems';
import { ItemsGroupByType, ItemsGroupByTypeKeys } from '../models/organization/settings/enum';
import { ProviderDetails } from '../models/datasource';
import { Datasources } from '../enums/datasources';

interface DiffKeyParts {
    pimsGeneratedId: string;
    siteId: string;
}

export class InvoiceItemsWithVariantDetails {
    constructor(private readonly groupBy: ItemsGroupByType = ItemsGroupByType.Id, private readonly masterSiteId?: string) {}

    static createDiffKey(pimsGeneratedId?: string, siteId?: string): string {
        return JSON.stringify({ pimsGeneratedId, siteId });
    }

    static splitDiffKey(diffKey: string): DiffKeyParts {
        const { pimsGeneratedId, siteId } = JSON.parse(diffKey);
        return { pimsGeneratedId, siteId };
    }

    transform(invoiceItems: InvoiceItem[]): InvoiceItemWithVariantDetail[] {
        const groupByKey = this.getGroupByKey();

        const groups = invoiceItems.reduce((agg, invoiceItem) => {
            const processedItemKey =
                (this.groupBy === ItemsGroupByType.Id && invoiceItem[groupByKey]) ||
                (invoiceItem.itemParent && `${invoiceItem[groupByKey].toLowerCase()}-sub`) ||
                invoiceItem[groupByKey].toLowerCase();

            const group = agg.get(processedItemKey) || [];

            agg.set(processedItemKey, [...group, invoiceItem]);

            return agg;
        }, new Map<string, InvoiceItem[]>());

        const result = [...groups.keys()].reduce((agg, key) => {
            const groupItems = groups.get(key) as InvoiceItem[];
            const masterSiteProduct = groupItems.find((item) => item.provider.siteId.toString() === this.masterSiteId) || groupItems[0];
            const difference = this.createDiff(groupItems, masterSiteProduct);

            agg.push({
                ...masterSiteProduct,
                difference,
                providers: groupItems.map((item) => item.provider),
                variants: groupItems,
            });

            return agg;
        }, [] as InvoiceItemWithVariantDetail[]);

        return new Transformers.CollectionTransformer(
            new Transformers.ToTypeTransformer<InvoiceItemWithVariantDetail>(InvoiceItemWithVariantDetail),
        ).transform(result);
    }

    private createDiff(items: InvoiceItem[], masterSiteItem: InvoiceItem): InvoiceItemData {
        return items.reduce(
            (agg, item) => {
                const { provider, ...itemCopy } = item;
                const masterSiteItemCopy = { ...masterSiteItem };
                const excludedKeys = this.groupBy === ItemsGroupByType.Description ? ['providers', 'pimsGeneratedId', 'updateDateTime'] : [];
                masterSiteItemCopy.provider = new ProviderDetails();

                masterSiteItemCopy.hospitalDescription = masterSiteItemCopy.hospitalDescription?.toLowerCase();
                itemCopy.hospitalDescription = itemCopy.hospitalDescription?.toLowerCase();

                const difference =
                    (masterSiteItemCopy.childItems?.length || 0) > (itemCopy.childItems?.length || 0)
                        ? Utils.difference<Partial<InvoiceItem>>(itemCopy, masterSiteItemCopy, excludedKeys)
                        : Utils.difference<Partial<InvoiceItem>>(masterSiteItemCopy, itemCopy, excludedKeys);

                if (difference.childItems && masterSiteItemCopy.childItems?.length === itemCopy.childItems?.length) {
                    (difference.childItems as InvoiceItemChild[]).forEach((child, index) => {
                        if (Object.keys(difference.childItems?.[index] || {}).length === 1) {
                            delete difference.childItems?.[index];
                        }
                    });
                }

                this.removeExemptVariantProperties(difference, provider, { value: itemCopy, other: masterSiteItemCopy });
                if (Object.keys(difference).length !== 0) {
                    const diffKey = InvoiceItemsWithVariantDetails.createDiffKey(itemCopy.pimsGeneratedId, provider.siteId);
                    agg[diffKey] = item;
                }
                return agg;
            },
            { master: { ...masterSiteItem } },
        );
    }

    private getGroupByKey(): ItemsGroupByTypeKeys {
        if (this.groupBy === ItemsGroupByType.Id) {
            return ItemsGroupByTypeKeys.PimsGeneratedId;
        }
        return ItemsGroupByTypeKeys.HospitalDescription;
    }

    /**
     * Remove item properties that should not be considered for variant calculations
     * @param difference - will be updated as a side-effect
     * @param provider
     */
    private removeExemptVariantProperties = (
        difference: Partial<InvoiceItem>,
        provider: ProviderDetails,
        items: { value: Partial<InvoiceItem>; other: Partial<InvoiceItem> },
    ): void => {
        // If we are not grouping by pimsGeneratedId, then we don't expect or care if these ids differ
        if (this.getGroupByKey() !== ItemsGroupByTypeKeys.PimsGeneratedId) {
            delete difference[ItemsGroupByTypeKeys.PimsGeneratedId];
        }

        if (provider?.name?.toLowerCase() === Datasources.ANIMANA.toLowerCase()) {
            // Animana classifications share name/descriptions, not pimsIds
            if (difference.classification) {
                delete difference.classification.pimsGeneratedId;
                delete difference.classification.pimsId;

                if (Object.keys(difference.classification).length === 0) {
                    delete difference.classification;
                }
            }

            // Animana parent items do not share pimsGenerated ids
            if (difference.itemParent) {
                // Animana subproducs with different parents are not considered variants
                delete difference.itemParent.hospitalDescription;

                delete difference.itemParent.pimsGeneratedId;

                if (Object.keys(difference.itemParent).length === 0) {
                    delete difference.itemParent;
                }
            }

            // Animana child items do not share pimsGenerated ids and provider
            if (difference.childItems) {
                difference.childItems = difference.childItems.reduce<InvoiceItemChild[]>((agg, { pimsGeneratedId, provider, ...child }) => {
                    if (Object.keys(child).length) {
                        agg.push(child as InvoiceItemChild);
                    }

                    return agg;
                }, []);

                if ((difference.childItems as InvoiceItemChild[]).every((child) => !child)) {
                    delete difference.childItems;
                }
            }

            if (difference.animanaSpecificFields) {
                const { value, other } = items;

                delete difference.animanaSpecificFields.productAnalysisGroup1Uuid;
                delete difference.animanaSpecificFields.productAnalysisGroup2Uuid;
                delete difference.animanaSpecificFields.productAnalysisGroup3Uuid;

                if (value.animanaSpecificFields?.supplierName?.toUpperCase() === other.animanaSpecificFields?.supplierName?.toUpperCase()) {
                    delete difference.animanaSpecificFields.supplierName;
                }
                if (
                    value.animanaSpecificFields?.productAnalysisGroup1Name?.toUpperCase() ===
                    other.animanaSpecificFields?.productAnalysisGroup1Name?.toUpperCase()
                ) {
                    delete difference.animanaSpecificFields.productAnalysisGroup1Name;
                }
                if (
                    value.animanaSpecificFields?.productAnalysisGroup2Name?.toUpperCase() ===
                    other.animanaSpecificFields?.productAnalysisGroup2Name?.toUpperCase()
                ) {
                    delete difference.animanaSpecificFields.productAnalysisGroup2Name;
                }
                if (
                    value.animanaSpecificFields?.productAnalysisGroup3Name?.toUpperCase() ===
                    other.animanaSpecificFields?.productAnalysisGroup3Name?.toUpperCase()
                ) {
                    delete difference.animanaSpecificFields.productAnalysisGroup3Name;
                }

                if (Object.keys(difference.animanaSpecificFields).length === 0) {
                    delete difference.animanaSpecificFields;
                }
            }
        }
    };
}
